import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../pipes/translate.pipe';
import { CodebookService } from './codebook.service';
import { IDefect } from './defect.models';

/**
 * Shown before reporting a defect from a room (functional area) detail: lists the
 * open defects already reported for the room, so the user does not report the
 * same defect again. Clicking a defect opens its detail; the new defect is
 * reported only after confirming.
 */
@Component({
  selector: 'app-functional-area-defects-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="fa-defects-dialog-backdrop" (click)="onCancel()">
      <div class="fa-defects-dialog" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h4 class="modal-title">Nahlášené závady</h4>
          @if (areaTitle) {
            <div class="modal-subtitle">{{ areaTitle }}</div>
          }
        </div>
        <div class="modal-message">
          V této místnosti už jsou nahlášené tyto závady. Než nahlásíte novou, zkontrolujte, zda mezi nimi není.
        </div>
        <ul class="modal-body">
          @for (defect of defects; track defect.id) {
            <li (click)="viewDefect.emit(defect)">
              <div class="defect-title">
                @if (getSeverityIconUrl(defect.severityLevelId); as iconUrl) {
                  <img class="defect-severity-icon" [src]="iconUrl" alt="" />
                }
                <span>{{ defect.title }}</span>
              </div>
              <div class="defect-meta">
                <span>{{ 'defectinfo_panel_state_label' | translate }}: {{ getStateTitle(defect) }}</span>
                <span>{{ 'defectinfo_panel_created_label' | translate }}: {{ defect.createdAt * 1000 | date:'dd.MM.yyyy HH:mm' }}</span>
              </div>
            </li>
          }
        </ul>
        <div class="modal-footer">
          <button type="button" class="link-btn" (click)="reportNew.emit()">
            Nahlásit novou závadu
          </button>
          <button type="button" class="link-btn secondary" (click)="onCancel()">
            Zrušit
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .fa-defects-dialog-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 4000;
      pointer-events: auto;
    }

    .fa-defects-dialog {
      background: white;
      border-radius: 5px;
      width: 500px;
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 60px);
      display: flex;
      flex-direction: column;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      pointer-events: auto;
    }

    .modal-header {
      padding: 15px 20px;
      border-bottom: 1px solid #ddd;

      .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 500;
        color: #333;
      }

      .modal-subtitle {
        margin-top: 4px;
        font-size: 14px;
        color: #666;
      }
    }

    .modal-message {
      padding: 15px 20px 5px;
      color: #666;
      font-size: 14px;
      line-height: 1.5;
    }

    .modal-body {
      list-style-type: none;
      margin: 0;
      padding: 10px 20px;
      overflow-y: auto;
      min-height: 0;

      li {
        padding: 10px;
        margin-bottom: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        cursor: pointer;
        color: #333;

        &:hover {
          background-color: #f5f5f5;
        }
      }
    }

    .defect-title {
      display: flex;
      align-items: center;
      font-weight: bold;
      word-break: break-word;
    }

    .defect-severity-icon {
      width: 20px;
      height: 20px;
      flex: none;
      margin-right: 5px;
      object-fit: contain;
    }

    .defect-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 4px 15px;
      margin-top: 5px;
      font-size: 13px;
      color: #666;
    }

    .modal-footer {
      padding: 15px 20px;
      border-top: 1px solid #ddd;
      display: flex;
      justify-content: flex-end;
      gap: 10px;

      .link-btn {
        border: none;
        color: var(--ci-emerald);
        background-color: var(--ci-electric);
        padding: 8px 15px;
        min-width: 100px;
        font-size: 14px;
        border-radius: 5px;
        text-align: center;
        cursor: pointer;

        &:hover {
          background-color: var(--ci-emerald);
          color: var(--ci-electric);
        }

        &.secondary {
          background-color: #999;

          &:hover {
            background-color: #777;
          }
        }
      }
    }
  `]
})
export class FunctionalAreaDefectsDialogComponent {
  @Input() defects: IDefect[] = [];
  @Input() areaTitle = '';
  @Output() viewDefect = new EventEmitter<IDefect>();
  @Output() reportNew = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  private codebookService = inject(CodebookService);

  getStateTitle(defect: IDefect): string {
    return this.codebookService.getDefectStateById(defect.defectStateId)?.title ?? '';
  }

  getSeverityIconUrl(severityId: number): string | null {
    switch (severityId) {
      case 1:
        return 'assets/images/defect/severity-high.png';
      case 2:
        return 'assets/images/defect/severity-medium.png';
      case 3:
        return 'assets/images/defect/severity-low.png';
      default:
        return null;
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
